import { callAngularMethod, injectAngularFragment } from '../../../utils/interop-angular';

/** @type {(obj: unknown) => obj is PromiseLike<any>} */
const isPromise = obj => obj && 'then' in obj && typeof obj.then === 'function';

/**
 * @param {string | URL} current
 * @param {import('../usePlanPickerApi/items').PlanPickerItem} selectedItem
 */
function constructNewUrl(current, selectedItem) {
    const newUrl = new URL(current);
    newUrl.searchParams.set('p', selectedItem.id);
    return newUrl;
}

/**
 * @param {import('../usePlanPickerApi/items').PlanPickerItem} selectedItem
 * @returns {PlanChangeEvent}
 */
function createPlanChangeEvent(selectedItem) {
    return {
        url: constructNewUrl(window.location.href, selectedItem),
        selectedItem
    };
}

/** @type {PlanChangeEffect} */
export function updateHiddenPlanInput({ selectedItem }) {
    // Some other effects will try to get the current plan value by calling
    // `npg.getPlanValue()`. The implementation of that method checks the
    // plan picker value and, failing that, falls back to a hidden input,
    // #hidPlatformInput, that's included on every page in the PageHeader
    // partial.
    $('#hidPlatformName').val(selectedItem.id);
}

/** @type {PlanChangeEffect} */
export function logPlanChangeAnalyticsEvent({ selectedItem }) {
    switch (selectedItem.kind) {
        case 'company':
            npg.logAnalyticsEvent('global-companyselect');
            break;
        case 'plan':
            npg.logAnalyticsEvent('global-planselect');
            break;
        default:
            // "unknown" items and all others -- do nothing
            break;
    }
}

/** @type {PlanChangeEffect} */
export function redirect({ url }) {
    window.location = url.href;
}

/**
 * @param {PlanChangeEvent} event
 * @returns {Promise<StyleData>}
*/
async function fetchStyleDetails(event) {
        const styleResponse = await fetch(`/Common/Navigation/StyleDetails?p=${event.selectedItem.id}`, {
            method: 'POST'
        })

        const styleData = await styleResponse.json();

        const styleUrlsToFetch = $('link[rel=stylesheet][data-palettized]')
            .map((i, linkEl) => {
                const url = new URL(linkEl.href);
                url.searchParams.set('id', styleData.PaletteId);
                return url.href;
            }).toArray();

        await Promise.all(
            styleUrlsToFetch.map(async url => {
                const response = await fetch(url);
                return await response.text();
            })
        );

        return styleData;
}

/**
 * @param {string} targetId
 * @param {{ fallback?: PlanChangeEffect }} [options]
 * @returns {PlanChangeEffect}
 */
export function createLoadHtmlEffect(targetId, { fallback } = {}) {
    // eslint-disable-next-line consistent-return
    return async function loadDynamicHtmlEffect(event) {
        const { url } = event;

        try {
            let [templateResponse, styleData] = await Promise.all([
                fetch(url, {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                }),
                fetchStyleDetails(event)
            ]);
            const html = await templateResponse.text();
            // To avoid the picker loading twice. We need to remove
            // the one from the response
            const parsed = $.parseHTML(html, null, true);
            const removedPlanPickerHTML = $(parsed).filter(':not(div#plandropdown)');
            await injectAngularFragment(targetId, removedPlanPickerHTML);
            await updateStyles(styleData);

            // The fetch request, Angular injection, and style update
            // can all fail, so only run these side effects at the end
            // once we know they've succeeded.
            npg.logAnalyticsView(url.href);

            npg.ngApp.util.setPlanIdInUrl(url.searchParams.get('p'));

        } catch (e) {
            if (fallback) {
                return fallback(event);
            } else {
                throw e;
            }
        }
    };
}

/**
 * @param {string} targetId
 * @param {string} methodName
 * @param {{ fallback?: PlanChangeEffect }} [options]
 * @returns {PlanChangeEffect}
 */
export function createCallAngularMethodEffect(targetId, methodName, { fallback } = {}) {
    // eslint-disable-next-line consistent-return
    return async function callAngularMethodEffect(event) {
        const { url } = event;
        try {
            callAngularMethod(targetId, methodName, url.searchParams.get('p'));
            const styleData = await fetchStyleDetails(event);
            await updateStyles(styleData);

        } catch (e) {
            if (fallback) {
                return fallback(event);
            } else {
                throw e;
            }
        }
    };
}

/** @param {StyleData} styleData */
async function updateStyles(styleData) {
    // Update logos
    $('#headerLogo')
        .attr('src', styleData.HeaderLogoPath)
        .toggleClass('default', styleData.IsDefaultLogo)
        .toggleClass('custom', !styleData.IsDefaultLogo);
    if (styleData.FooterLogoPath) {
        $('#footerLogo').attr('src', styleData.FooterLogoPath).show();
    } else {
        $('#footerLogo').hide();
    }

     // update palette
    $('link[rel=stylesheet][data-palettized]').each(async (i, linkEl) => {
        const url = new URL(linkEl.href);
        if (Number(url.searchParams.get('id')) === styleData.PaletteId) {
            return;
        }
        // Reduce flicker by preloading
        url.searchParams.set('id', styleData.PaletteId);
        // add new link tag to head with new href value
        const newLink = document.createElement('link');
        newLink.href = url.href;
        newLink.rel = 'stylesheet';
        newLink.dataset['palettized'] = 'true';
        document.head.appendChild(newLink);

        // then remove old link tag
        // waiting for a few seconds in order to allow the new file to finish
        // downloading if it hasn't been cached, and also to separate the paints
        // to avoid FOUT
        setTimeout(() => {
            linkEl.remove();
        }, 2500);
    });

}

/**
 * @param {PlanChangeEffect[]} effects
 * @param {PlanChangeEvent} event
 */
async function runEffects(effects, event) {
    npg.showBusyIndicator();
    try {
        for (const effect of effects) {
            const result = effect(event);
            if (isPromise(result)) {
                await result;
            }
        }
    } finally {
        npg.hideBusyIndicator();
    }
}

/**
 * @param {PlanChangeEffect[]} effects
 * @returns {(selectedItem: import("../usePlanPickerApi/items").PlanPickerItem) => Promise<void>}
 */
export function createChangeHandler(effects) {
    return selectedItem => {
        const event = createPlanChangeEvent(selectedItem);
        return runEffects(effects, event);
    };
}

/**
 * @typedef {{ FooterLogoPath: string, HeaderLogoPath: string, IsDefaultLogo: boolean, PaletteId: number }} StyleData
 */

/**
 * @typedef {(event: PlanChangeEvent) => (void | Promise<void>)} PlanChangeEffect
 */

/**
 * @typedef {{ url: URL, selectedItem: import('../usePlanPickerApi/items').PlanPickerItem }} PlanChangeEvent
 */
